import React from "react";
import { Link } from "react-router-dom";
import { SurveyAnswers } from "../../common/hooks/api/useSurvey";
import { OrganizationDataRecipientDto } from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { SurveyQuestionProps } from "../components/get-compliant/survey/SurveyQuestion";
import { getVendorsByCategory } from "../pages/get-compliant/data-recipients/shared";
import { Routes } from "../root/routes";
import { SurveyDefinition } from "./survey";
import { alphabetically, noneCheckboxOption, vendorToCheckboxOption } from "./util";
import { CONTRACTOR_CATEGORY, VENDOR_CATEGORY } from "../types/Vendor";
import { parseCheckboxSurveyAnswer } from "../util/surveyAnswerParsing";

const sharingInstructions = (): SurveyQuestionProps => {
  return {
    type: "acknowledgement",
    question: "What is targeted advertising?",
    slug: "sharing-definition-acknowledgement",
    text: (
      <>
        Consumers have the right to opt-out of targeted advertising (called "sharing" under the
        CCPA). Targeted advertising is advertising that is based on a person's online activity as it
        is tracked across different websites or devices, usually through an online identifier like a
        cookie or pixel placed on your website by an ad network like Facebook Ads.
      </>
    ),
  };
};

const SHARING_VENDOR_CATEGORIES = [
  VENDOR_CATEGORY.AD_NETWORK,
  VENDOR_CATEGORY.SALES_MARKETING_TOOL,
  VENDOR_CATEGORY.DATA_ANALYTICS_PROVIDER,
  VENDOR_CATEGORY.DATA_BROKER,
  "Data Cooperative",
];

export const isEligibleSharingVendor = (vendor: OrganizationDataRecipientDto): boolean =>
  SHARING_VENDOR_CATEGORIES.includes(vendor.category) &&
  vendor.collectionContext.includes("CONSUMER") &&
  vendor.ccpaIsSharing === true;

export const CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG = "custom-audience-feature-separate-vendor";
export const customAudienceSeparateVendorQuestion = (
  vendors: OrganizationDataRecipientDto[],
  vendorsRoute: string,
): SurveyQuestionProps => {
  const salesVendors = getVendorsByCategory(vendors, VENDOR_CATEGORY.SALES_MARKETING_TOOL);
  const salesContractors = getVendorsByCategory(vendors, CONTRACTOR_CATEGORY.SALES_MARKETING);
  const coMarketingPartners = getVendorsByCategory(vendors, VENDOR_CATEGORY.CO_MARKETING_PARTNER);
  const customAudienceManagedVendorsOptions = [
    ...salesVendors,
    ...salesContractors,
    ...coMarketingPartners,
  ]
    .sort(alphabetically)
    .map((sv) => vendorToCheckboxOption(sv));
  customAudienceManagedVendorsOptions.unshift(noneCheckboxOption());

  return {
    type: "checkboxes",
    slug: CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG,
    question:
      "Does your business manage custom audience lists for any ad networks through a separate vendor or platform? Select all vendors from the list below.",
    helpText: (
      <>
        For example, some businesses control audience lists through a marketing tool like Klaviyo or
        Listrak. The list below includes your Data Recipients categorized as Sales & Marketing
        Tools, Co-Marketing Partners, or Sales & Marketing Contractors. Don't see your platform
        manager in this list? Go to{" "}
        <Link className={"bold-link"} to={vendorsRoute}>
          Vendors
        </Link>{" "}
        to add it or to recategorize a custom vendor.
      </>
    ),
    options: customAudienceManagedVendorsOptions,
    visible: true,
  };
};

/**
 * Generates individual questions for each selected managing vendor to ask which ad networks they manage
 * These questions don't need survey slugs since data is stored directly in organization_managed_ad_network table
 */
export const generateCustomAudienceVendorManagedNetworkQuestions = (
  vendors: OrganizationDataRecipientDto[],
  selectedVendorManagers: string,
): SurveyQuestionProps[] => {
  // Parse the selected vendor managers
  const selectedManagerIds = parseCheckboxSurveyAnswer(selectedVendorManagers);

  if (selectedManagerIds.length === 0) {
    return [];
  }

  // Get sharing vendors configured to use custom audiences in the data map
  const selectedSharingVendors = vendors.filter(
    (vendor) => isEligibleSharingVendor(vendor) && vendor.usesCustomAudience === true,
  );

  if (selectedSharingVendors.length === 0) {
    return [];
  }

  // Get the selected managing vendors
  const selectedManagers = vendors.filter((v) => selectedManagerIds.includes(v.id));

  const questions: SurveyQuestionProps[] = [];

  // Create a question for each managing vendor
  selectedManagers.forEach((manager) => {
    const sharingVendorOptions = selectedSharingVendors
      .sort(alphabetically)
      .map((vendor) => vendorToCheckboxOption(vendor));

    questions.push({
      type: "checkboxes",
      // Use a special prefix to indicate this is a non-persisted UI-only question
      slug: `__ui_only__custom-audience-managed-by-${manager.id}`,
      question: `Which vendor's custom audience lists are managed via ${
        manager.name || "this vendor"
      }?`,
      options: sharingVendorOptions,
      selectAllOption: false,
    });
  });

  return questions;
};

export const DIRECT_MAIL_VENDORS_SLUG = "direct-mail-vendors";

// POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
// export const facebookLduQuestion = (): SurveyQuestionProps => {
//   return {
//     type: "boolean",
//     slug: "facebook-ldu",
//     question: "Will your business enable Limited Data Use (LDU) for Facebook Ads?",
//     helpText: (
//       <>
//         You're not required to enable LDU. Enabling LDU will "turn off" personalized advertising via
//         Facebook Ads for all California visitors to your website. This may impact your advertising
//         for California web visitors, but it means California consumers will not need to click a link
//         on your website to opt-out of "sharing" with Facebook Ads.{" "}
//         <a
//           href="https://www.facebook.com/business/help/1151133471911882"
//           target="_blank"
//           rel="noreferrer noopener"
//         >
//           How to enable LDU
//         </a>
//         .
//       </>
//     ),
//   };
// };

// export const googleRdpQuestion = (): SurveyQuestionProps => {
//   return {
//     type: "boolean",
//     slug: "google-rdp",
//     question: "Will your business enable Restricted Data Processing (RDP) for Google Ads?",
//     helpText: (
//       <>
//         You're not required to enable RDP. Enabling RDP will "turn off" personalized advertising via
//         Google Ads for all California visitors to your website. This may impact your advertising for
//         California web visitors, but it means California consumers will not need to click a link on
//         your website to opt-out of "sharing" with Google Ads.{" "}
//         <a
//           href="https://support.google.com/google-ads/answer/9606827#zippy=%2Cedit-your-global-site-tag-to-disable-ad-personalization-signals"
//           target="_blank"
//           rel="noreferrer noopener"
//         >
//           How to enable RDP
//         </a>{" "}
//         (be sure to update your global site tag).
//       </>
//     ),
//   };
// };

const sharingQuestions = (
  _answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
): SurveyQuestionProps[] => {
  const baseQuestions = [
    sharingInstructions(), // "sharing-definition-acknowledgement"
    // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
    // facebookLduQuestion(), // "facebook-ldu"
    // googleRdpQuestion(), // "google-rdp"
    customAudienceSeparateVendorQuestion(vendors, Routes.getCompliant.dataRecipients.root), // "custom-audience-feature-separate-vendor"
  ];

  // Add individual questions for each managing vendor
  const vendorNetworkQuestions = generateCustomAudienceVendorManagedNetworkQuestions(
    vendors,
    _answers[CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG],
  );

  return [...baseQuestions, ...vendorNetworkQuestions];
};

const sharingVisibility = (
  answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
  overrideAcknowledgement = false,
): Record<string, boolean> => {
  // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
  const answeredAcknowledgement =
    overrideAcknowledgement || Boolean(answers["sharing-definition-acknowledgement"]);

  const answeredSharesDataQuestion =
    !!answers["share-data-for-advertising"] && answers["share-data-for-advertising"].length > 0;
  const hasAnsweredSharingVendors =
    answers["share-data-for-advertising"] == "false" || vendors.some(isEligibleSharingVendor);
  // Parse the checkbox arrays properly
  const customAudienceVendorIds: string[] = parseCheckboxSurveyAnswer(
    answers[CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG],
  );

  const usesCustomAudienceFeature = vendors.some(
    (vendor) => isEligibleSharingVendor(vendor) && vendor.usesCustomAudience === true,
  );
  const usesCustomAudienceVendorFeature =
    customAudienceVendorIds.length > 0 && !customAudienceVendorIds.includes("none");

  const baseVisibility = {
    "share-data-for-advertising": answeredAcknowledgement,
    // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
    // "facebook-ldu":
    //   answers["share-data-for-advertising"] == "true" && Boolean(findFacebookAds(sharingVendors)),
    // "google-rdp":
    //   answers["share-data-for-advertising"] == "true" && Boolean(findGoogleAds(sharingVendors)),
    [CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG]:
      answeredAcknowledgement &&
      answeredSharesDataQuestion &&
      hasAnsweredSharingVendors &&
      usesCustomAudienceFeature,
  };

  // Add visibility for individual vendor network questions (UI-only)
  if (usesCustomAudienceVendorFeature) {
    customAudienceVendorIds.forEach((managerId) => {
      if (managerId !== "none") {
        baseVisibility[`__ui_only__custom-audience-managed-by-${managerId}`] = true;
      }
    });
  }

  return baseVisibility;
};

export const sharingSurvey = (
  answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
  overrideAcknowledgement = false,
): SurveyDefinition => {
  return {
    questions: sharingQuestions(answers, vendors),
    visibility: sharingVisibility(answers, vendors, overrideAcknowledgement),
    disabled: {},
  };
};

export const CUSTOM_AUDIENCE_VENDOR_MANAGED_SHARING_VENDORS_FEATURE_SLUG =
  "custom-audience-feature-separate-vendor-managed-sharing-vendors";

/**
 * Aggregates all the individual vendor managed sharing vendor answers into a single array
 * for backward compatibility with existing code that expects the old format
 */
export const getAggregatedVendorManagedSharingVendors = (answers: SurveyAnswers): string[] => {
  const aggregatedVendors: string[] = [];

  // Find all answers that start with "__ui_only__custom-audience-managed-by-"
  Object.keys(answers).forEach((slug) => {
    if (slug.startsWith("__ui_only__custom-audience-managed-by-")) {
      const vendorIds = parseCheckboxSurveyAnswer(answers[slug]);
      vendorIds.forEach((vendorId) => {
        if (!aggregatedVendors.includes(vendorId)) {
          aggregatedVendors.push(vendorId);
        }
      });
    }
  });

  return aggregatedVendors;
};

/**
 * Gets vendor managed sharing vendors in the old format for backward compatibility
 * Returns a JSON string array like the old question used to
 *
 * @deprecated This function name is kept for backward compatibility.
 * Use getAggregatedVendorManagedSharingVendors for new code.
 */
export const getVendorManagedAdNetworksIds = (answers: SurveyAnswers): string => {
  const vendorIds = getAggregatedVendorManagedSharingVendors(answers);
  return JSON.stringify(vendorIds);
};
